import { Text } from "@react-three/drei";
import { Vector3 } from "@react-three/fiber";
import { assetPath } from "@utils/Config";
import { FiberUtils } from "@utils/FiberUtils";
import { MathUtils } from "@utils/MathUtils";
import { Raycast } from "@utils/Raycast";
import { useEffect, useMemo } from "react";
import { BufferGeometry, ColorRepresentation, Float32BufferAttribute, LineBasicMaterial, LineSegments, Matrix4 } from "three";
import { Quaternion, Vector3 as ThreeVector3 } from "three";
import { Line2, LineGeometry, LineMaterial } from "three/examples/jsm/Addons";

type Mark = {
    position: number;
    value: string;
}

type AxisProps = {
    position?: Vector3;
    direction: Vector3;
    color?: ColorRepresentation;
    label: string;
    labelPosition?: "above" | "below";
    legend?: Mark[];
    length: number;
    arrowLength?: number;
    legendLength?: number;
    thickness?: number;
    opacity?: number;
}

function Axis({
    position, direction, color = "#000000",
    label, labelPosition = "below",
    legend,
    length, arrowLength = length * 0.05, legendLength = length * 0.03,
    thickness = 2, opacity = 1
}: AxisProps) {
    const [forward, up, right] = useMemo(() => {
        const forward = FiberUtils.toVector3(direction).normalize();
        const up = forward.y > 0.999 ? new ThreeVector3(-1, 0, 0) : new ThreeVector3(0, 1, 0);
        const right = new ThreeVector3().crossVectors(forward, up).normalize();
        up.crossVectors(right, forward).normalize();
        return [forward, up, right];
    }, [direction]);
    const [end, head1, head2, markLabelPositions, ...marks] = useMemo(() => {
        const end = forward.clone().multiplyScalar(length);
        const head1 = forward.clone().applyAxisAngle(right, Math.PI * 0.2);
        const head2 = forward.clone().applyAxisAngle(right, -Math.PI * 0.2);
        head1.subVectors(end, head1.multiplyScalar(arrowLength));
        head2.subVectors(end, head2.multiplyScalar(arrowLength));
        const markLabelPositions = [];
        const marks = [];
        if (legend) {
            const offset = up.clone().multiplyScalar(legendLength);
            for (let i = 0; i < legend.length; ++i) {
                const mark = forward.clone().multiplyScalar(legend[i].position);
                marks.push(mark.clone());
                mark.add(offset);
                marks.push(mark.clone());
                mark.sub(offset);
                mark.sub(offset);
                marks.push(mark.clone());
                mark.add(offset);
                marks.push(mark.clone());
                if (labelPosition === "below")
                    mark.sub(offset.clone().multiplyScalar(3));
                else
                    mark.add(offset.clone().multiplyScalar(3));
                markLabelPositions.push(mark);
            }
        }
        return [end, head1, head2, markLabelPositions, ...marks];
    }, [forward, up, right, legend, length, arrowLength, legendLength, labelPosition]);

    const geometry = useMemo(() => {
        const lines = [
            0, 0, 0, ...end,
            ...head1, ...end,
            ...head2, ...end,
            ...marks.flatMap(m => [m.x, m.y, m.z])
        ];
        const geometry = new LineGeometry().setPositions(lines);
        // for (let i = 0; i < lines.length; i += 2) geometry.addGroup(i, 2, 0);
        return geometry;
    }, [end, head1, head2, marks]);

    const material = useMemo(() => {
        const material = new LineMaterial({ color, transparent: true, linewidth: thickness });
        return material;
    }, [color, thickness]);

    const line = useMemo(() => {
        const line = new Line2(geometry, material);
        (line.raycast as any) = Raycast.Disabled;
        return line;
    }, [geometry, material]);

    const labelTransform = useMemo(() => {
        const matrix = new Matrix4().makeBasis(forward, up, right);
        const quat = new Quaternion().setFromRotationMatrix(matrix);
        let offset = legend ? 0.35 : 0.25;
        if (labelPosition === "below") offset *= -1;
        const pos = end.clone().add(new ThreeVector3(0, offset, 0).applyQuaternion(quat));
        return {
            position: pos,
            quaternion: quat,
            scale: 0.15,
        }
    }, [forward, up, right, end, legend, labelPosition]);

    useEffect(() => {
        material.opacity = opacity;
        material.depthWrite = opacity > 0;
    }, [material, opacity]);

    return (
        <group position={position}>
            <primitive object={line} />
            <group {...labelTransform}>
                <Text
                    color={color} font={assetPath("/fonts/SohoGothicPro-Regular.otf")}
                    anchorX="right"
                    fillOpacity={opacity} material-depthWrite={opacity > 0}
                >
                    {label}
                </Text>
            </group>
            {legend && <group>
                {legend.map((mark, index) => (
                    <Text key={`legend-${mark.value}`}
                        position={markLabelPositions[index]}
                        scale={0.12}
                        color={color} font={assetPath("/fonts/SohoGothicPro-Regular.otf")}
                        anchorX="center"
                        fillOpacity={opacity} material-depthWrite={opacity > 0}
                    >
                        {mark.value}
                    </Text>
                ))}
            </group>}
        </group>
    );
}

type GraphProps = {
    points: Vector3[];
    color?: ColorRepresentation;
    thickness?: number;
    drawAxes?: boolean | [x: boolean, y: boolean];
    axesColor?: ColorRepresentation | [x: ColorRepresentation, y: ColorRepresentation];
    axesThickness?: number | [x: number, y: number];
    legend?: [x: number[], y: number[]];
    legendSuffix?: [x: string, y: string];
    labels?: [x: string, y: string];
    xRange: [from: number, to: number];
    yRange: [from: number, to: number];
    xSize?: number;
    ySize?: number;
    opacity?: number;
}

export function Graph({
    points, color = "#000000", thickness = 1,
    drawAxes = true, axesColor = "#000000", axesThickness = 2,
    legend, legendSuffix, labels = ["x", "y"],
    xRange, yRange,
    xSize = 1, ySize = 1,
    opacity = 1
}: GraphProps) {
    const geometry = useMemo(() => {
        const positions = points.flatMap((point, index) => {
            const vec = FiberUtils.toVector3(point);
            vec.x = MathUtils.mapRange(vec.x, xRange[0], xRange[1], 0, xSize);
            vec.y = MathUtils.mapRange(vec.y, yRange[0], yRange[1], 0, ySize);
            const values = vec.toArray();
            if (index === 0 || index === points.length - 1) return [...values, ...values];
            return values;
        });
        const geometry = new LineGeometry().setPositions(positions);
        return geometry;
    }, [points, xRange[0], xRange[1], xSize, yRange[0], yRange[1], ySize]); // eslint-disable-line react-hooks/exhaustive-deps

    const material = useMemo(() => {
        const material = new LineMaterial({ color, transparent: true, linewidth: thickness });
        return material;
    }, [color, thickness]);

    const line = useMemo(() => {
        const line = new Line2(geometry, material);
        line.raycast = Raycast.Disabled!;
        return line;
    }, [geometry, material]);

    useEffect(() => {
        material.opacity = opacity;
        material.depthWrite = opacity > 0;
    }, [material, opacity]);

    drawAxes = Array.isArray(drawAxes) ? drawAxes : [drawAxes, drawAxes];
    axesColor = Array.isArray(axesColor) ? axesColor : [axesColor, axesColor];
    axesThickness = Array.isArray(axesThickness) ? axesThickness : [axesThickness, axesThickness];

    return (
        <>
            {drawAxes[0] && <Axis direction={[1, 0, 0]} color={axesColor[0]}
                position={[0, MathUtils.mapRange(0, yRange[0], yRange[1], 0, ySize), 0]}
                legend={legend?.[0].map(value => ({ position: MathUtils.mapRange(value, xRange[0], xRange[1], 0, xSize), value: `${value}${legendSuffix?.[0] ?? ""}` }))}
                label={labels[0]}
                thickness={axesThickness[0]}
                length={xSize} arrowLength={0.1} legendLength={0.05}
                opacity={opacity}
            />}
            {drawAxes[1] && <Axis direction={[0, 1, 0]} color={axesColor[1]}
                legend={legend?.[1].map(value => ({ position: MathUtils.mapRange(value, yRange[0], yRange[1], 0, ySize), value: `${value}${legendSuffix?.[1] ?? ""}` }))}
                label={labels[1]} labelPosition="above"
                thickness={axesThickness[1]}
                length={ySize} arrowLength={0.1} legendLength={0.05}
                opacity={opacity}
            />}
            <primitive object={line} />
        </>
    );
}

type GridProps = {
    xLines: number[];
    yLines: number[];
    xSpan: [number, number];
    ySpan: [number, number];
    xRange: [number, number];
    yRange: [number, number];
    xSize: number;
    ySize: number;
    color?: ColorRepresentation;
    opacity?: number;
}

export function Grid({
    xLines, yLines, xSpan, ySpan,
    xRange, yRange, xSize, ySize,
    color = '#ffffff', opacity = 1
}: GridProps) {
    const gridObject = useMemo(() => {
        const positions: number[] = [];
        for (const x of xLines) {
            const sx = MathUtils.mapRange(x, xRange[0], xRange[1], 0, xSize);
            const sy1 = MathUtils.mapRange(ySpan[0], yRange[0], yRange[1], 0, ySize);
            const sy2 = MathUtils.mapRange(ySpan[1], yRange[0], yRange[1], 0, ySize);
            positions.push(sx, sy1, 0, sx, sy2, 0);
        }
        for (const y of yLines) {
            const sy = MathUtils.mapRange(y, yRange[0], yRange[1], 0, ySize);
            const sx1 = MathUtils.mapRange(xSpan[0], xRange[0], xRange[1], 0, xSize);
            const sx2 = MathUtils.mapRange(xSpan[1], xRange[0], xRange[1], 0, xSize);
            positions.push(sx1, sy, 0, sx2, sy, 0);
        }
        const geometry = new BufferGeometry();
        geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
        const material = new LineBasicMaterial({ color, transparent: true, opacity });
        const lines = new LineSegments(geometry, material);
        (lines.raycast as any) = Raycast.Disabled;
        return lines;
    }, [xLines, yLines, xSpan, ySpan, xRange, yRange, xSize, ySize, color]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        (gridObject.material as LineBasicMaterial).opacity = opacity;
        (gridObject.material as LineBasicMaterial).depthWrite = opacity > 0;
    }, [gridObject, opacity]);

    return <primitive object={gridObject} />;
}

export function sampleCurve(curve: (time: number) => number, samples: number, from: number = 0, to: number = 1, z: number = 0) {
    const step = (to - from) / (samples - 1);
    const results: Array<Vector3> = [];
    for (let i = 0; i < samples; ++i) {
        const time = from + step * i;
        results.push([time, curve(time), z]);
    }
    return results;
}